import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { addToCart } from "../../lib/cart";
import { Heart, Image as ImageIcon, Minus, Plus, ShoppingBag, Truck, MapPin, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { ProductCard } from "../../components/shared/ProductCard";
import { mapApiProduct } from "../../data/mockData";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedChoices, setSelectedChoices] = useState({});
  const [activeImage, setActiveImage] = useState(0);
  const [favorite, setFavorite] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [availability, setAvailability] = useState(null);
  const [others, setOthers] = useState([]);
  const localMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const [calMonth, setCalMonth] = useState(() => localMonth(new Date()));
  const [calendar, setCalendar] = useState(null);
  // Navigation bornée : pas avant le mois courant, jusqu'à 24 mois dans le futur
  const minMonth = localMonth(new Date());
  const maxMonth = localMonth(new Date(new Date().getFullYear(), new Date().getMonth() + 24, 1));

  useEffect(() => {
    api.get(`/availability/calendar/${slug}`, { params: { month: calMonth } })
      .then((r) => setCalendar(r.data))
      .catch(() => setCalendar(null));
  }, [slug, calMonth]);

  const shiftMonth = (delta) => {
    // Arithmétique pure sur AAAA-MM — jamais de Date (piège du fuseau horaire)
    const [y, m] = calMonth.split("-").map(Number);
    const total = y * 12 + (m - 1) + delta;
    const next = `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
    if (next >= minMonth && next <= maxMonth) setCalMonth(next);
  };

  useEffect(() => {
    api.get(`/catalog/products/${slug}`)
      .then((r) => setProduct(mapApiProduct(r.data)))
      .catch(() => setProduct("not-found"));
  }, [slug]);

  useEffect(() => {
    if (!product || product === "not-found") return;
    api.get("/catalog/products", { params: { category: product.category } })
      .then((r) => setOthers(r.data.map(mapApiProduct).filter((p) => p.slug !== product.slug).slice(0, 4)))
      .catch(() => setOthers([]));
  }, [product]);

  useEffect(() => {
    if (startDate && endDate && endDate >= startDate) {
      api.get("/availability/check", { params: { slug, start_date: startDate, end_date: endDate } })
        .then((r) => setAvailability(r.data))
        .catch(() => setAvailability(null));
    }
  }, [startDate, endDate, slug]);

  if (!product) {
    return <div className="container-ae py-24 text-center font-heading text-deepgreen" data-testid="product-loading">Chargement…</div>;
  }

  if (product === "not-found") {
    return (
      <div className="container-ae py-24 text-center" data-testid="product-not-found">
        <h1 className="font-heading text-3xl text-deepgreen">Article introuvable</h1>
        <p className="mt-3 text-sm text-muted-foreground">Cet article n'existe pas ou n'est plus disponible à la location.</p>
        <Link to="/boutique" className="btn-primary-ae mt-8 inline-flex" data-testid="product-back-catalog">Découvrir la collection</Link>
      </div>
    );
  }

  const images = (product.images || []).filter(Boolean);
  const selectedChoicesList = (product.option_groups || [])
    .map((g) => ({ group: g, choice: (g.choices || []).find((c) => c.id === selectedChoices[g.id]) }))
    .filter((x) => x.choice);
  const choiceSupp = selectedChoicesList.reduce((s, x) => s + (x.choice.per_unit ? x.choice.price_ttc * qty : x.choice.price_ttc), 0);
  const missingRequired = (product.option_groups || []).filter((g) => g.required && !selectedChoices[g.id]);

  return (
    <div className="container-ae py-12 md:py-16" data-testid="product-page">
      <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
        <Link to="/boutique" className="hover:text-deepgreen" data-testid="breadcrumb-boutique">Boutique</Link>
        <span className="mx-2">/</span>
        <span className="text-deepgreen">{product.name}</span>
      </nav>
      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        <div>
          <div className="overflow-hidden rounded-xl bg-cream-dark">
            {images.length > 0 ? (
              <img src={images[activeImage]} alt={product.name} className="aspect-square w-full object-cover" data-testid="product-main-image" />
            ) : (
              <div className="flex aspect-square w-full flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="product-main-image">
                <ImageIcon className="h-10 w-10" aria-hidden />
                <p className="text-sm">Photo à venir</p>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImage(i)}
                  data-testid={`product-thumbnail-${i}`}
                  className={`overflow-hidden rounded-lg border-2 transition-colors ${activeImage === i ? "border-deepgreen" : "border-transparent hover:border-sage"}`}
                  aria-label={`Photo ${i + 1}`}
                >
                  <img src={img} alt="" className="aspect-square w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl text-deepgreen" data-testid="product-name">{product.name}</h1>
              <p className="mt-3 text-2xl font-heading font-semibold text-deepgreen" data-testid="product-price">
                {(product.price + choiceSupp).toFixed(2)} € <span className="text-sm font-body font-normal text-muted-foreground">TTC / weekend</span>
                {choiceSupp > 0 && <span className="ml-2 align-middle text-xs font-body font-semibold text-gold" data-testid="product-price-choices">dont options : +{choiceSupp.toFixed(2)} €</span>}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFavorite(!favorite)}
              data-testid="product-favorite-button"
              aria-label="Ajouter aux favoris"
              className={`rounded-full border p-3 transition-colors ${favorite ? "border-gold bg-gold-light text-gold" : "border-border text-deepgreen hover:border-gold"}`}
            >
              <Heart className={`h-5 w-5 ${favorite ? "fill-current" : ""}`} />
            </button>
          </div>

          <div className="mt-6 rounded-lg bg-sage-light p-4" data-testid="product-availability">
            <p className="text-sm font-semibold text-deepgreen">Disponibilité</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Choisissez vos dates pour vérifier la disponibilité réelle — calculée par quantité et par période (contrôle effectué côté serveur dans un lot ultérieur).
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label-ae" htmlFor="date-debut">Début de location</label>
                <input id="date-debut" type="date" className="input-ae" data-testid="product-date-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <label className="label-ae" htmlFor="date-fin">Fin de location</label>
                <input id="date-fin" type="date" className="input-ae" data-testid="product-date-end" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Période standard : vendredi → dimanche soir, adaptable.</p>
            {availability && (
              <p className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold ${availability.available ? "bg-sage/20 text-deepgreen" : "bg-red-50 text-red-700"}`} data-testid="product-availability-result">
                {availability.available
                  ? `Disponible sur cette période (${availability.remaining} restant${availability.remaining > 1 ? "s" : ""})`
                  : "Indisponible sur cette période"}
                {" "}— indicatif, le contrôle définitif a lieu côté serveur à l'envoi de la demande.
              </p>
            )}
          </div>

          {/* Calendrier de disponibilité réelle du mois (données du moteur de stock) */}
          {calendar && (
            <div className="mt-4 rounded-lg border border-border bg-white p-4" data-testid="availability-calendar">
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => shiftMonth(-1)} disabled={calMonth <= minMonth} className="rounded-full border border-border px-3 py-1 text-xs font-bold text-deepgreen hover:bg-sage-light disabled:cursor-not-allowed disabled:opacity-40" data-testid="cal-prev-month" aria-label="Mois précédent">←</button>
                <p className="text-sm font-semibold capitalize text-deepgreen" data-testid="cal-month-label">
                  {new Date(`${calMonth}-02`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                </p>
                <button type="button" onClick={() => shiftMonth(1)} disabled={calMonth >= maxMonth} className="rounded-full border border-border px-3 py-1 text-xs font-bold text-deepgreen hover:bg-sage-light disabled:cursor-not-allowed disabled:opacity-40" data-testid="cal-next-month" aria-label="Mois suivant">→</button>
              </div>
              <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"].map((d) => <span key={d}>{d}</span>)}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1" data-testid="cal-grid">
                {(() => {
                  const dayKeys = Object.keys(calendar.days);
                  const firstOffset = (new Date(`${dayKeys[0]}T12:00:00`).getDay() + 6) % 7;
                  const nowLocal = new Date();
                  const todayIso = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, "0")}-${String(nowLocal.getDate()).padStart(2, "0")}`;
                  const cells = [];
                  for (let i = 0; i < firstOffset; i++) cells.push(<span key={`b${i}`} />);
                  dayKeys.forEach((dk) => {
                    const d = calendar.days[dk];
                    const past = dk < todayIso;
                    const clickable = !past && d.available;
                    const inRange = startDate && endDate && dk >= startDate && dk <= endDate;
                    const isEdge = dk === startDate || dk === endDate;
                    const pick = () => {
                      if (!startDate || endDate || dk < startDate) { setStartDate(dk); setEndDate(""); }
                      else { setEndDate(dk); window.scrollTo({ top: 0, behavior: "smooth" }); }
                    };
                    cells.push(
                      <button key={dk} type="button" disabled={!clickable} onClick={clickable ? pick : undefined} data-testid={`cal-day-${dk}`}
                        title={`${dk} — ${past ? "passé" : d.available ? `${d.remaining} disponible${d.remaining > 1 ? "s" : ""} — cliquez pour choisir cette date` : "complet"}`}
                        className={`flex h-8 flex-col items-center justify-center rounded-md text-[11px] font-semibold transition-colors ${past ? "bg-cream text-muted-foreground/50" : !d.available ? "bg-red-50 text-red-700" : isEdge ? "bg-deepgreen text-cream" : inRange ? "bg-sage/60 text-deepgreen" : "cursor-pointer bg-sage/30 text-deepgreen hover:bg-sage/60"}`}>
                        <span>{Number(dk.slice(8))}</span>
                        {!past && d.available && !isEdge && <span className="text-[8px] leading-none">{d.remaining} dispo</span>}
                      </button>
                    );
                  });
                  return cells;
                })()}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Vert = disponible (quantité restante) · rouge = complet · grisé = passé. Cliquez sur un jour disponible pour pré-remplir vos dates de location ci-dessus. Indicatif — le contrôle définitif a lieu à l'envoi de votre demande.</p>
            </div>
          )}

          {product.variants?.length > 0 && (
            <div className="mt-6">
              <label className="label-ae">Variante</label>
              <div className="flex flex-wrap gap-2" data-testid="product-variants">
                {product.variants.map((v) => (
                  <button key={v.id} type="button" onClick={() => setVariant(variant === v.id ? null : v.id)} data-testid={`variant-${v.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${variant === v.id ? "border-deepgreen bg-sage-light text-deepgreen" : "border-border bg-white text-deepgreen hover:border-deepgreen"}`}>
                    {v.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.options?.length > 0 && (
            <div className="mt-6">
              <label className="label-ae">Options</label>
              <div className="space-y-2" data-testid="product-options">
                {product.options.map((o) => (
                  <label key={o.id} className="flex items-center gap-3 rounded-lg border border-border bg-white px-4 py-3 text-sm">
                    <input type="checkbox" className="h-4 w-4 accent-[#2C4C3B]" checked={selectedOptions.includes(o.id)} onChange={() => setSelectedOptions((prev) => prev.includes(o.id) ? prev.filter((x) => x !== o.id) : [...prev, o.id])} data-testid={`option-${o.id}`} />
                    <span className="flex-1">{o.name}</span>
                    {o.price_ttc > 0 && <span className="text-xs font-semibold text-deepgreen">+{o.price_ttc.toFixed(2)} €{o.per_unit ? " /u" : ""}</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {product.option_groups?.length > 0 && product.option_groups.map((g) => (
            <div className="mt-6" key={g.id}>
              <label className="label-ae">{g.name} — {g.required ? "obligatoire" : "facultatif"}</label>
              <div className="flex flex-wrap gap-2" data-testid={`option-group-${g.id}`}>
                {g.choices.map((c) => {
                  const selected = selectedChoices[g.id] === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedChoices((prev) => {
                        const next = { ...prev };
                        if (next[g.id] === c.id) { if (!g.required) delete next[g.id]; } else next[g.id] = c.id;
                        return next;
                      })}
                      data-testid={`choice-${g.id}-${c.id}`}
                      aria-pressed={selected}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${selected ? "border-deepgreen bg-sage-light text-deepgreen" : "border-border bg-white text-deepgreen hover:border-deepgreen"}`}
                    >
                      {c.name}{c.price_ttc > 0 && ` · +${c.price_ttc.toFixed(2)} €`}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center rounded-full border border-border bg-white">
              <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-deepgreen" data-testid="qty-decrease" aria-label="Diminuer la quantité">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center text-sm font-bold text-deepgreen" data-testid="qty-value">{qty}</span>
              <button type="button" onClick={() => setQty(qty + 1)} className="p-3 text-deepgreen" data-testid="qty-increase" aria-label="Augmenter la quantité">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              disabled={missingRequired.length > 0}
              onClick={() => {
                addToCart({
                  slug: product.slug, name: product.name, price: product.price, image: product.image, qty,
                  variant_id: variant, option_ids: selectedOptions, caution_amount: product.caution_amount || 0,
                  option_choice_ids: selectedChoicesList.map((x) => `${x.group.id}:${x.choice.id}`),
                  option_choices: selectedChoicesList.map((x) => ({ group_name: x.group.name, choice_name: x.choice.name, price_ttc: x.choice.price_ttc, per_unit: !!x.choice.per_unit })),
                });
                toast.success("Article ajouté à votre sélection");
              }}
              className="btn-primary-ae flex-1 disabled:opacity-50"
              data-testid="add-to-selection-button"
            >
              <ShoppingBag className="h-4 w-4" /> Ajouter à ma sélection
            </button>
          </div>
          {missingRequired.length > 0 && (
            <p className="mt-2 text-xs font-semibold text-gold" data-testid="required-groups-hint">
              Choix obligatoire : {missingRequired.map((g) => g.name).join(", ")}
            </p>
          )}

          <div className="mt-8 grid gap-3 rounded-xl bg-white p-5 text-sm" data-testid="product-essential-info">
            <p className="flex items-center gap-2.5 text-foreground/80"><MapPin className="h-4 w-4 text-sage" /> Retrait gratuit au dépôt d'Artres, sur rendez-vous</p>
            <p className="flex items-center gap-2.5 text-foreground/80"><Truck className="h-4 w-4 text-sage" /> Livraison jusqu'à 80 km — 20 km offerts, offerte dès 250 € de matériel</p>
            <p className="flex items-center gap-2.5 text-foreground/80"><RotateCcw className="h-4 w-4 text-sage" /> Date et créneau de retour validés avec Art Event</p>
            <p className="flex items-center gap-2.5 text-foreground/80"><ShieldCheck className="h-4 w-4 text-sage" /> Caution demandée séparément — jamais confondue avec le total</p>
          </div>
        </div>
      </div>

      {others.length > 0 && (
        <section className="mt-20" data-testid="product-complementary">
          <h2 className="font-heading text-2xl text-deepgreen">Souvent associés</h2>
          <p className="mt-1 text-sm text-muted-foreground">Recommandations définies par Art Event — jamais de coordination proposée ici.</p>
          <div className="mt-6 grid grid-cols-2 gap-6 lg:grid-cols-4">
            {others.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
